import { styled } from "@mui/material/styles";
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from "@mui/material";
import { ReactNode } from "react";

export type ButtonVariant = "primary" | "white" | "green";
export type ButtonSize = "s" | "m";

interface StyledButtonProps {
  btnvariant?: ButtonVariant;
  btnsize?: ButtonSize;
}

interface CustomButtonProps
  extends Omit<MuiButtonProps, "variant" | "children" | "size"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
}

const StyledButton = styled(MuiButton)<StyledButtonProps>(
  ({ btnvariant = "primary", btnsize = "s" }) => {
    const fontSize = btnsize === "m" ? "18px" : "16px";
    const lineHeight = btnsize === "m" ? "22px" : "19px";
    const fontWeight = btnvariant === "green" ? 500 : 400;

    const baseStyles = {
      fontSize,
      fontWeight,
      lineHeight,
      padding: "12px 20px",
      borderRadius: "12px",
      textTransform: "none" as const,
      fontFamily: "var(--font-family)",
      letterSpacing: "0px",
      gap: "8px",
      whiteSpace: "nowrap" as const,
      transition: "background-color 0.2s ease, border-color 0.2s ease",
    };

    const variantStyles = (() => {
      switch (btnvariant) {
        case "primary":
          return {
            background: "var(--brand-primary)",
            color: "var(--text-white)",
            border: "none",
            "&:hover": {
              background: "var(--brand-primary-hover)",
            },
            "&:disabled": {
              background: "var(--button-hover-light)",
              color: "var(--text-primary)",
              opacity: 0.6,
            },
          };
        case "white":
          return {
            background: "var(--background-white)",
            color: "var(--text-primary)",
            border: "1px solid var(--button-border)",
            "&:hover": {
              background: "var(--button-hover-light)",
              border: "1px solid var(--button-border)",
            },
            "&:disabled": {
              background: "var(--background-white)",
              color: "var(--text-primary)",
              border: "1px solid var(--button-border)",
              opacity: 0.6,
            },
          };
        case "green":
          return {
            background: "var(--background-white)",
            color: "var(--brand-primary)",
            border: "none",
            "&:hover": {
              background: "var(--button-hover-light)",
            },
            "&:disabled": {
              background: "var(--background-white)",
              color: "var(--brand-primary)",
              opacity: 0.6,
            },
          };
        default:
          return {};
      }
    })();

    return {
      ...baseStyles,
      ...variantStyles,
    };
  }
);

export const Button = ({
  children,
  variant = "primary",
  size = "s",
  fullWidth = false,
  ...props
}: CustomButtonProps) => {
  return (
    <StyledButton {...props} btnvariant={variant} btnsize={size} fullWidth={fullWidth}>
      {children}
    </StyledButton>
  );
};
